import { z } from 'zod';
import { GIT_LIST_LIMIT_DEFAULT, GIT_LIST_LIMIT_MAX, GitChangeRequestState, GitCiStatus, GitDeploymentStatus, GitLinkTargetKind, GitTimeMeta, } from './git.js';
import { IssueState } from './issue.js';
import { WorkflowCategory } from './project.js';
/**
 * Tab Git — CI, bản phát hành, deployment (APM-GIT-i). docs/plan-git-integration.md
 * §5.3, §5.5, §7.1, §8.1 (màn "Bản phát hành"), §8.2 (chip trên task).
 *
 * Tệp riêng (không nối vào `git.ts`) để các task `APM-GIT-*` chạy song song không
 * giẫm lên nhau. Hai phần:
 *
 * - **Hàm thuần** `estimateDeployment` / `estimateDeployedAt` — ước lượng "MR này
 *   lên production lúc nào" (§7.1). API (chip, sự kiện `deployed`), web
 *   (`cycleBreakdown`) và chỉ số của `APM-GIT-j` dùng chung **một** định nghĩa.
 * - **Hình API** của bốn endpoint đọc ở `git-releases.controller.ts`.
 */
// ── `deployed_at` của một MR — ước lượng (§7.1) ─────────────────────────────
/** Trần commit của một khoảng `prev_tag..tag` (§5.5); vượt ⇒ `rangeStatus = 'truncated'`. */
export const GIT_RELEASE_RANGE_MAX_COMMITS = 2000;
function instantMs(value) {
    if (value === null || value === undefined)
        return null;
    const ms = value instanceof Date ? value.getTime() : Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
}
/**
 * Deployment đã đưa một MR lên production — **ước lượng** (§7.1), theo đúng thứ tự:
 *
 * 1. deployment `success` **sớm nhất** có `sha` = `merge_commit_sha` (hoặc
 *    `squash_commit_sha` — squash + fast-forward của GitLab không để lại commit
 *    merge; GitHub squash thì `merge_commit_sha` chính là commit squash) ⇒ `exact`;
 * 2. không có ⇒ deployment `success` sớm nhất có `finished_at ≥ merged_at` trên
 *    **cùng nhánh đích** (`ref` = `target_branch`).
 *
 * `null` khi MR chưa merge, hoặc chưa có deployment nào thoả. `asOf` (luật #7):
 * chỉ tính deployment đã xong lúc ấy.
 *
 * ⚠️ Là ước lượng, và giao diện phải nói thế: bước 2 coi "mọi thứ đã merge vào
 * nhánh trước lượt triển khai" là đã lên — sai khi lượt ấy triển khai một commit
 * cũ hơn (rollback). Kho triển khai **từ tag** (`ref` là tên tag, không phải nhánh)
 * chỉ khớp được ở bước 1.
 */
export function estimateDeployment(changeRequest, deployments, asOf = null) {
    const merged = instantMs(changeRequest.mergedAt);
    if (merged === null)
        return null;
    const limit = instantMs(asOf);
    if (limit !== null && merged > limit)
        return null;
    const done = deployments
        .map((deployment) => ({ deployment, at: instantMs(deployment.finishedAt) }))
        .filter((row) => row.deployment.status === 'success' &&
        row.at !== null &&
        (limit === null || row.at <= limit))
        .sort((a, b) => a.at - b.at);
    const shas = [changeRequest.mergeCommitSha, changeRequest.squashCommitSha].filter((sha) => sha !== null && sha !== '');
    const exact = done.find((row) => shas.includes(row.deployment.sha));
    if (exact)
        return { deployment: exact.deployment, at: new Date(exact.at), exact: true };
    const after = done.find((row) => row.at >= merged && row.deployment.ref === changeRequest.targetBranch);
    return after ? { deployment: after.deployment, at: new Date(after.at), exact: false } : null;
}
/** Mốc "lên production" của một MR — đầu vào `deployedAt` của `cycleBreakdown`. */
export function estimateDeployedAt(changeRequest, deployments, asOf = null) {
    return estimateDeployment(changeRequest, deployments, asOf)?.at ?? null;
}
// ── Hình API (đọc theo thời gian — luật #7) ─────────────────────────────────
const GitListLimit = z.coerce.number().int().min(1).max(GIT_LIST_LIMIT_MAX);
export const GitReleaseRangeStatus = z.enum(['pending', 'done', 'truncated']);
/**
 * Một bản phát hành (tag khớp `tagPattern` của kho).
 *
 * Tồn tại tại `asOf` ⇔ `releasedAt ≤ asOf`. ⚠️ Thứ **không** dựng lại được: tập
 * việc thuộc bản (`itemCount`) là kết quả của lần tính gần nhất, theo liên kết
 * hiệu lực **lúc tính** — không phải lúc `asOf`; `prevTag` cũng là giá trị hiện tại.
 */
export const GitReleaseView = z.object({
    id: z.uuid(),
    repositoryId: z.uuid(),
    repositoryPath: z.string(),
    tag: z.string(),
    /** Tên bản phát hành ở máy chủ git; `null` = tag trần, không có release. */
    name: z.string().nullable(),
    sha: z.string(),
    /** Tag không có release ⇒ ngày của commit mà tag trỏ tới. */
    releasedAt: z.iso.datetime(),
    webUrl: z.string().nullable(),
    isPrerelease: z.boolean(),
    /** Tag khớp `tagPattern` liền trước; `null` = bản đầu tiên (chỉ tính trong tầm nhập). */
    prevTag: z.string().nullable(),
    /** Số commit của khoảng `prevTag..tag`; `null` = chưa tính. */
    commitCount: z.number().int().nonnegative().nullable(),
    /**
     * `pending` — chưa tính xong việc thuộc bản · `done` · `truncated` — khoảng vượt
     * `GIT_RELEASE_RANGE_MAX_COMMITS` commit (hoặc máy chủ git cắt danh sách): danh
     * sách việc **có thể thiếu**, giao diện phải nói rõ.
     */
    rangeStatus: GitReleaseRangeStatus,
    /** Số việc (task + issue) thuộc bản, kể cả việc đã xoá sau đó. */
    itemCount: z.number().int().nonnegative(),
});
/** `GET …/git/releases` — mới nhất trước, `releasedAt ≤ asOf`. */
export const GitReleaseListQuery = z.object({
    repositoryId: z.uuid().optional(),
    cursor: z.string().max(400).optional(),
    limit: GitListLimit.default(GIT_LIST_LIMIT_DEFAULT),
    asOf: z.iso.datetime().optional(),
});
export const GitReleasePage = GitTimeMeta.extend({
    items: z.array(GitReleaseView),
    nextCursor: z.string().nullable(),
});
/**
 * Một việc thuộc bản phát hành (§5.5). Khác `GitLinkedTarget` (chỉ mang mã): màn
 * "Bản phát hành" nhóm theo loại việc và hiện tên + trạng thái — đọc qua scope của
 * dự án như mọi câu đọc task khác (luật #5). Việc đã xoá không trả về.
 *
 * ⚠️ `title`, `typeName`, `status*` luôn là giá trị **hiện tại**, không phải lúc `asOf`.
 */
export const GitReleaseItemView = z.object({
    kind: GitLinkTargetKind,
    id: z.uuid(),
    /** `GOLD-123` · `GOLD-I12`. */
    code: z.string(),
    title: z.string(),
    /** Loại việc (task: tên `task_type`; issue: tên loại issue); `null` = chưa phân loại. */
    typeName: z.string().nullable(),
    /** Task: tên cột workflow. Issue: `null` — xem `issueState`. */
    statusName: z.string().nullable(),
    /** Task: nhóm của cột. Issue: `done` khi đã giải quyết / đóng, `todo` khi mới, còn lại `in_progress`. */
    statusCategory: WorkflowCategory,
    /** Chỉ với issue. */
    issueState: IssueState.nullable(),
    /** MR đã mang việc này vào bản; `null` = vào qua commit trực tiếp (không qua MR nào). */
    via: z
        .object({
        number: z.number().int().positive(),
        title: z.string(),
        state: GitChangeRequestState,
        webUrl: z.string(),
    })
        .nullable(),
});
/**
 * Một lượt triển khai. `status` là trạng thái **tại `asOf`**: lượt tạo trước mốc
 * mà xong sau mốc thì lúc ấy còn `running` (và `finishedAt` là `null`).
 */
export const GitDeploymentView = z.object({
    id: z.uuid(),
    repositoryId: z.uuid(),
    repositoryPath: z.string(),
    externalId: z.string(),
    environment: z.string(),
    /** Môi trường này là `productionEnvironment` của kho. */
    isProduction: z.boolean(),
    ref: z.string(),
    sha: z.string(),
    status: GitDeploymentStatus,
    createdAtExt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
    webUrl: z.string().nullable(),
});
/** `GET …/git/releases/:releaseId` — bản phát hành, việc thuộc bản, deployment liên quan. */
export const GitReleaseDetail = GitTimeMeta.extend({
    release: GitReleaseView,
    /** Xếp theo loại việc rồi theo mã — giao diện nhóm theo `typeName`. */
    items: z.array(GitReleaseItemView),
    /** Deployment của cùng kho có `sha` = commit của tag, hoặc triển khai thẳng từ tag (`ref` = tag). */
    deployments: z.array(GitDeploymentView),
});
/** `GET …/git/deployments` — mới nhất trước, `createdAtExt ≤ asOf`. */
export const GitDeploymentListQuery = z.object({
    repositoryId: z.uuid().optional(),
    /** Tên môi trường, khớp đúng từng ký tự. Vắng = mọi môi trường đã kéo về. */
    environment: z.string().trim().min(1).max(100).optional(),
    cursor: z.string().max(400).optional(),
    limit: GitListLimit.default(GIT_LIST_LIMIT_DEFAULT),
    asOf: z.iso.datetime().optional(),
});
export const GitDeploymentPage = GitTimeMeta.extend({
    items: z.array(GitDeploymentView),
    nextCursor: z.string().nullable(),
});
/**
 * Một lượt CI của nhánh mặc định (hoặc nhánh khớp `releaseBranchPattern`).
 * `status` là trạng thái **tại `asOf`**: lượt xong sau mốc thì lúc ấy còn `running`.
 */
export const GitPipelineView = z.object({
    id: z.uuid(),
    repositoryId: z.uuid(),
    repositoryPath: z.string(),
    externalId: z.string(),
    ref: z.string(),
    sha: z.string(),
    status: GitCiStatus,
    webUrl: z.string().nullable(),
    createdAtExt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
    durationS: z.number().int().nonnegative().nullable(),
});
export const GIT_PIPELINE_LIMIT_DEFAULT = 20;
/**
 * `GET …/git/pipelines` — các lượt CI gần đây nhất, `createdAtExt ≤ asOf`. Vắng
 * `ref` = nhánh **mặc định** của từng kho.
 */
export const GitPipelineListQuery = z.object({
    repositoryId: z.uuid().optional(),
    ref: z.string().trim().min(1).max(255).optional(),
    limit: GitListLimit.default(GIT_PIPELINE_LIMIT_DEFAULT),
    asOf: z.iso.datetime().optional(),
});
export const GitPipelineList = GitTimeMeta.extend({ items: z.array(GitPipelineView) });
//# sourceMappingURL=git-release.js.map